import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faUserCheck,
  faBullhorn,
  faHandshake,
  faThumbsUp,
  faTools,
  faGamepad,
  faDollarSign,
  faRobot,
  faDatabase,
  faMoneyBill,
  faUsers,
  faGift,
  faEnvelope,
  faShareAlt,
  faCheck,
  faVial,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import Header from '../../component/layout/header';
import Footer from '../../component/layout/footer';
import Navigate from '../../../../util/Navigate';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import IndieSubmenu from '../../component/section/publishers/IndieSubmenu';

function PublisherPricingPage() {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <>
      <Helmet prioritizeSeoTags>
        <meta charSet="utf-8" />
        <title>Platform Pricing | Glitch Gaming</title>
        <meta
          name="description"
          content="Discover the cost-effective pricing plans for Glitch's performance-based influencer marketing services. Maximize your game's exposure with authentic, quality content from micro-influencers."
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Platform Pricing For Influencer Marketing | Glitch"
        />
        <meta
          property="og:description"
          content="Unlock the power of performance-based influencer marketing for your game. Glitch offers innovative solutions for authenticity, quality content, and optimized platform utilization to maximize game exposure and engage effectively with micro-influencers."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta
          property="og:image"
          content="https://www.glitch.fun/assets/images/publishers/banner_3.png"
        />
        <meta
          name="keywords"
          content="influencer marketing, game marketing, performance-based marketing, micro-influencers, marketing pricing plans, Glitch"
        />
        <link rel="canonical" href={currentUrl} />
      </Helmet>
      <Header />
      
      {/* Hero Banner */}
      <div className="container-fluid pricing-hero-banner" style={bannerStyle}>
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <h1 className="display-4 fw-bold mb-4">
                Simple, Transparent Pricing for <span className="text-gradient">All Game Developers</span>
              </h1>
              <p className="lead mb-5">
                Choose the perfect plan for your studio size. Only pay for what you need with our flexible influencer packages.
              </p>
              <div className="d-flex justify-content-center gap-3">
                <Link 
                  to={Navigate.publishersOnboardingMultistepPage()} 
                  className="btn btn-danger btn-lg px-4"
                >
                  Get Started
                </Link>
                <a 
                  href="#pricing-plans" 
                  className="btn btn-outline-light btn-lg px-4"
                >
                  View Plans
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Plans Section */}
      <section id="pricing-plans" className="pricing-section py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3 text-black">Choose Your Plan</h2>
            <p className="lead text-muted"> No credit card required to sign-up.</p>
          </div>

          <div className="row g-4 justify-content-center">
            {/* Free Plan */}
            <div className="col-md-6 col-lg-4">
              <div className="card pricing-card h-100">
                <div className="card-header bg-white text-center py-4">
                  <h3 className="h4 mb-3">Free</h3>
                  <div className="price-display mb-3">
                    <span className="price-amount">$0</span>
                    <span className="price-period text-black">/month</span>
                  </div>
                  <Link 
                    to={Navigate.publishersOnboardingMultistepPage()} 
                    className="btn btn-outline-primary w-100"
                  >
                    Get Started
                  </Link>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled pricing-features">
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> Search Influencer Database</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> In-Depth Analytics</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 1 Social Post/Week</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> Community Forums</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> Media Kits (Watermarked)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Solo Dev Plan */}
            <div className="col-md-6 col-lg-4">
              <div className="card pricing-card popular h-100">
                <div className="popular-badge">Most Popular</div>
                <div className="card-header bg-primary text-white text-center py-4">
                  <h3 className="h4 mb-3">Solo Dev</h3>
                  <div className="price-display mb-3">
                    <span className="price-amount">$65</span>
                    <span className="price-period text-black">/month</span>
                  </div>
                  <Link 
                    to={Navigate.publishersOnboardingMultistepPage()} 
                    className="btn btn-light w-100"
                  >
                    Get Started
                  </Link>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled pricing-features">
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> <strong>2 Active Influencers</strong></li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 30 Outreach Credits</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 2 Social Posts/Week</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 10,000 Newsletter Emails</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 15 Play Tester Invites</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> Watermark-Free Assets</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Small Team Plan */}
            <div className="col-md-6 col-lg-4">
              <div className="card pricing-card h-100">
                <div className="card-header bg-white text-center py-4">
                  <h3 className="h4 mb-3">Small Team</h3>
                  <div className="price-display mb-3">
                    <span className="price-amount">$240</span>
                    <span className="price-period text-black">/month</span>
                  </div>
                  <Link 
                    to={Navigate.publishersOnboardingMultistepPage()} 
                    className="btn btn-primary w-100"
                  >
                    Get Started
                  </Link>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled pricing-features">
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> <strong>10 Active Influencers</strong></li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 100 Outreach Credits</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 10 Social Posts/Week</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 150,000 Newsletter Emails</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 30 Play Tester Invites</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> $0.80 Additional Credits</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Studio Plan */}
            <div className="col-md-6 col-lg-4">
              <div className="card pricing-card h-100">
                <div className="card-header bg-white text-center py-4">
                  <h3 className="h4 mb-3">Studio</h3>
                  <div className="price-display mb-3">
                    <span className="price-amount">$450</span>
                    <span className="price-period text-black">/month</span>
                  </div>
                  <Link 
                    to={Navigate.publishersOnboardingMultistepPage()} 
                    className="btn btn-primary w-100"
                  >
                    Get Started
                  </Link>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled pricing-features">
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> <strong>25 Active Influencers</strong></li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 200 Outreach Credits</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 20 Social Posts/Week</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 300,000 Newsletter Emails</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 45 Play Tester Invites</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> $0.60 Additional Credits</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Publisher Plan */}
            <div className="col-md-6 col-lg-4">
              <div className="card pricing-card h-100">
                <div className="card-header bg-white text-center py-4">
                  <h3 className="h4 mb-3">Publisher</h3>
                  <div className="price-display mb-3">
                    <span className="price-amount">$840</span>
                    <span className="price-period text-black">/month</span>
                  </div>
                  <Link 
                    to={Navigate.publishersOnboardingMultistepPage()} 
                    className="btn btn-primary w-100"
                  >
                    Get Started
                  </Link>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled pricing-features">
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> <strong>40 Active Influencers</strong></li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 350 Outreach Credits</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 30 Social Posts/Week</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 500,000 Newsletter Emails</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 60 Play Tester Invites</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> $0.50 Additional Credits</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Enterprise Plan */}
            <div className="col-md-6 col-lg-4">
              <div className="card pricing-card h-100">
                <div className="card-header bg-dark text-white text-center py-4">
                  <h3 className="h4 mb-3">Enterprise</h3>
                  <div className="price-display mb-3">
                    <span className="price-amount">Custom</span>
                  </div>
                  <Link
                    to={Navigate.demoPage()}
                    className="btn btn-outline-light w-100"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Contact Sales
                  </Link>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled pricing-features">
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> <strong>50+ Active Influencers</strong></li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> Custom Outreach Credits</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> Unlimited Social Posts</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> Custom Newsletter Volume</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> 100+ Play Tester Invites</li>
                    <li><FontAwesomeIcon icon={faCheck} className="text-success me-2" /> Dedicated Account Manager</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Comparison Section */}
      <section className="feature-comparison py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3">Detailed Feature Comparison</h2>
            <p className="lead text-white">See how our plans stack up against each other</p>
          </div>

          <div className="accordion" id="featureAccordion">
            {/* Game Analytics */}
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseAnalytics">
                  <FontAwesomeIcon icon={faChartLine} className="me-3 text-white'" /> <span className='text-white'>Game Analytics Features</span>
                </button>
              </h2>
              <div id="collapseAnalytics" className="accordion-collapse collapse show" data-bs-parent="#featureAccordion">
                <div className="accordion-body p-0">
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0">
                      <thead>
                        <tr>
                          <th>Feature</th>
                          <th className="text-center">Free</th>
                          <th className="text-center">Solo Dev</th>
                          <th className="text-center">Small Team</th>
                          <th className="text-center">Studio</th>
                          <th className="text-center">Publisher</th>
                          <th className="text-center">Enterprise</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>In-Depth Steam Reports</td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                        </tr>
                        <tr>
                          <td>Retention Reports</td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                        </tr>
                        <tr>
                          <td>Active User Reports</td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                        </tr>
                        <tr>
                          <td>Web Analytics</td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                        </tr>
                        <tr>
                          <td>Cross Device Tracking</td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                        </tr>
                      </tbody>
                      
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Influencer Features */}
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseInfluencers">
                  <span className='text-white'><FontAwesomeIcon icon={faUsers} className="me-3 text-white'" /> Influencer Features</span>
                </button>
              </h2>
              <div id="collapseInfluencers" className="accordion-collapse collapse" data-bs-parent="#featureAccordion">
                <div className="accordion-body p-0">
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0">
                      <thead>
                        <tr>
                          <th>Feature</th>
                          <th className="text-center">Free</th>
                          <th className="text-center">Solo Dev</th>
                          <th className="text-center">Small Team</th>
                          <th className="text-center">Studio</th>
                          <th className="text-center">Publisher</th>
                          <th className="text-center">Enterprise</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Active Influencers</td>
                          <td className="text-center">0</td>
                          <td className="text-center">2</td>
                          <td className="text-center">10</td>
                          <td className="text-center">25</td>
                          <td className="text-center">40</td>
                          <td className="text-center">50+</td>
                        </tr>
                        <tr>
                          <td>Outreach Credits</td>
                          <td className="text-center">-</td>
                          <td className="text-center">30</td>
                          <td className="text-center">100</td>
                          <td className="text-center">200</td>
                          <td className="text-center">350</td>
                          <td className="text-center">Custom</td>
                        </tr>
                        <tr>
                          <td>Additional Credits Cost</td>
                          <td className="text-center">-</td>
                          <td className="text-center">$1.50</td>
                          <td className="text-center">$0.80</td>
                          <td className="text-center">$0.60</td>
                          <td className="text-center">$0.50</td>
                          <td className="text-center">Custom</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media Features */}
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseSocial">
                  <span className='text-white'><FontAwesomeIcon icon={faShareAlt} className="me-3 text-white'" /> Social Media Features</span>
                </button>
              </h2>
              <div id="collapseSocial" className="accordion-collapse collapse" data-bs-parent="#featureAccordion">
                <div className="accordion-body p-0">
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0">
                      <thead>
                        <tr>
                          <th>Feature</th>
                          <th className="text-center">Free</th>
                          <th className="text-center">Solo Dev</th>
                          <th className="text-center">Small Team</th>
                          <th className="text-center">Studio</th>
                          <th className="text-center">Publisher</th>
                          <th className="text-center">Enterprise</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Max Posts Per Week</td>
                          <td className="text-center">1</td>
                          <td className="text-center">2</td>
                          <td className="text-center">10</td>
                          <td className="text-center">20</td>
                          <td className="text-center">30</td>
                          <td className="text-center">Unlimited</td>
                        </tr>
                        <tr>
                          <td>Analytics On Social Content</td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                          <td className="text-center"><FontAwesomeIcon icon={faCheck} className="text-success" /></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Community Features */}
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseCommunity">
                  <span className='text-white'><FontAwesomeIcon icon={faUsers} className="me-3 text-white'" /> Community Features</span>
                </button>
              </h2>
              <div id="collapseCommunity" className="accordion-collapse collapse" data-bs-parent="#featureAccordion">
                <div className="accordion-body p-0">
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0">
                      <thead>
                        <tr>
                          <th>Feature</th>
                          <th className="text-center">Free</th>
                          <th className="text-center">Solo Dev</th>
                          <th className="text-center">Small Team</th>
                          <th className="text-center">Studio</th>
                          <th className="text-center">Publisher</th>
                          <th className="text-center">Enterprise</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Watermark On Showcases</td>
                          <td className="text-center">Yes</td>
                          <td className="text-center">No</td>
                          <td className="text-center">No</td>
                          <td className="text-center">No</td>
                          <td className="text-center">No</td>
                          <td className="text-center">No</td>
                        </tr>
                        <tr>
                          <td>Watermark On Mediakit</td>
                          <td className="text-center">Yes</td>
                          <td className="text-center">No</td>
                          <td className="text-center">No</td>
                          <td className="text-center">No</td>
                          <td className="text-center">No</td>
                          <td className="text-center">No</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Newsletter Features */}
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseNewsletter">
                  <span className='text-white'><FontAwesomeIcon icon={faEnvelope} className="me-3 text-white'" /> Newsletter Features</span>
                </button>
              </h2>
              <div id="collapseNewsletter" className="accordion-collapse collapse" data-bs-parent="#featureAccordion">
                <div className="accordion-body p-0">
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0">
                      <thead>
                        <tr>
                          <th>Feature</th>
                          <th className="text-center">Free</th>
                          <th className="text-center">Solo Dev</th>
                          <th className="text-center">Small Team</th>
                          <th className="text-center">Studio</th>
                          <th className="text-center">Publisher</th>
                          <th className="text-center">Enterprise</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Monthly Newsletter Emails</td>
                          <td className="text-center">0</td>
                          <td className="text-center">10,000</td>
                          <td className="text-center">150,000</td>
                          <td className="text-center">300,000</td>
                          <td className="text-center">500,000</td>
                          <td className="text-center">Custom</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Play Tester Features */}
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapsePlaytest">
                  <span className='text-white'><FontAwesomeIcon icon={faVial} className="me-3 text-white'" /> Play Tester Features</span>
                </button>
              </h2>
              <div id="collapsePlaytest" className="accordion-collapse collapse" data-bs-parent="#featureAccordion">
                <div className="accordion-body p-0">
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0">
                      <thead>
                        <tr>
                          <th>Feature</th>
                          <th className="text-center">Free</th>
                          <th className="text-center">Solo Dev</th>
                          <th className="text-center">Small Team</th>
                          <th className="text-center">Studio</th>
                          <th className="text-center">Publisher</th>
                          <th className="text-center">Enterprise</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Play Tester Invites</td>
                          <td className="text-center">0</td>
                          <td className="text-center">15</td>
                          <td className="text-center">30</td>
                          <td className="text-center">45</td>
                          <td className="text-center">60</td>
                          <td className="text-center">100+</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5">
            <h4>Plan Details</h4>
            <div className="alert alert-info">
              <p className="mb-2">
                <strong>Active Influencers:</strong> The active influencers count represents the number of influencers considered active on a campaign in a given month, and either are producing content or their past content is being tracked.
              </p>
              <p className="mb-2">
                <strong>Overcharge:</strong> If you work with more influencers than allotted in your package, you will be charged the overcharge for each additional influencer.
              </p>
              <p className="mb-2">
                <strong>Outreach Credits:</strong> You can invite influencers to your campaign through our database. Each invite will count as one credit.
              </p>
              <p className="mb-0">
                <strong>Additional Credits:</strong> The cost of each additional credit to send an invite once your monthly limit is used up.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Free Features Section */}
      <section className="free-features py-5 bg-light">
        <div className="container text-black">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3 text-black">Free Features</h2>
            <p className="lead text-muted">Try these features without any commitment</p>
          </div>
          
          <div className="row g-4">
            <div className="col-md-6 col-lg-4">
              <div className="feature-card p-4 h-100">
                <FontAwesomeIcon icon={faGamepad} className="feature-icon text-primary mb-3" />
                <h4 className='text-black'>Campaign Creation</h4>
                <p>Create marketing campaigns with or without AI assistance to promote your game.</p>
              </div>
            </div>
            <div className="col-md-6 col-lg-4">
              <div className="feature-card p-4 h-100">
                <FontAwesomeIcon icon={faDatabase} className="feature-icon text-primary mb-3" />
                <h4 className='text-black'>Influencer Database</h4>
                <p>Search through our extensive database of gaming influencers to find perfect matches.</p>
              </div>
            </div>
            <div className="col-md-6 col-lg-4">
              <div className="feature-card p-4 h-100">
                <FontAwesomeIcon icon={faRobot} className="feature-icon text-primary mb-3" />
                <h4 className='text-black'>AI Recommendations</h4>
                <p>Get AI-powered influencer recommendations tailored to your game's genre and audience.</p>
              </div>
            </div>
            <div className="col-md-6 col-lg-4">
              <div className="feature-card p-4 h-100">
                <FontAwesomeIcon icon={faChartLine} className="feature-icon text-primary mb-3" />
                <h4 className='text-black'>Influencer Stats</h4>
                <p>View detailed statistics and analytics for all influencers in our database.</p>
              </div>
            </div>
            <div className="col-md-6 col-lg-4">
              <div className="feature-card p-4 h-100">
                <FontAwesomeIcon icon={faTools} className="feature-icon text-primary mb-3" />
                <h4 className='text-black'>Campaign Assets</h4>
                <p>Generate professional marketing assets for your campaigns.</p>
              </div>
            </div>
            <div className="col-md-6 col-lg-4">
              <div className="feature-card p-4 h-100">
                <FontAwesomeIcon icon={faHandshake} className="feature-icon text-primary mb-3" />
                <h4 className='text-black'>Consultation</h4>
                <p>Get a free 30-minute consultation with our marketing experts.</p>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-5">
            <Link to={Navigate.publishersOnboardingMultistepPage()} className="btn btn-primary btn-lg px-5">
              Start Using Free Features
            </Link>
          </div>
        </div>
      </section>

      {/* Managed Services Section */}
      <section className="managed-services py-5 bg-dark text-white">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3">Managed Services</h2>
            <p className="lead">
              Let us handle the heavy lifting while you focus on developing amazing games
            </p>
          </div>
          
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card bg-secondary text-white">
                <div className="card-body p-4">
                  <ul className="list-group list-group-flush mb-4">
                    <li className="list-group-item bg-transparent text-white border-light">
                      <FontAwesomeIcon icon={faEnvelope} className="me-3" />
                      Manage Newsletter Sending
                    </li>
                    <li className="list-group-item bg-transparent text-white border-light">
                      <FontAwesomeIcon icon={faShareAlt} className="me-3" />
                      Manage Social Media
                    </li>
                    <li className="list-group-item bg-transparent text-white border-light">
                      <FontAwesomeIcon icon={faBullhorn} className="me-3" />
                      Manage Influencer Outreach
                    </li>
                    <li className="list-group-item bg-transparent text-white border-light">
                      <FontAwesomeIcon icon={faUsers} className="me-3" />
                      Manage Community Engagement
                    </li>
                    <li className="list-group-item bg-transparent text-white border-light">
                      <FontAwesomeIcon icon={faGamepad} className="me-3" />
                      Manage Gaming Showcases
                    </li>
                  </ul>
                  
                  <div className="text-center">
                    <p className="lead mb-4">
                      Management plans for indie developers start as low as <strong>$150 per month</strong>.
                    </p>
                    <Link
                      to={Navigate.demoPage()}
                      className="btn btn-light btn-lg px-5"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Schedule a Meeting
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Learn More Section */}
      <IndieSubmenu />

      {/* Final CTA */}
      <section className="final-cta py-5 bg-danger text-white">
        <div className="container text-center">
          <h2 className="display-5 fw-bold mb-4">Ready to Boost Your Game's Visibility?</h2>
          <p className="lead mb-5">
            Start with our free plan and upgrade when you're ready to scale
          </p>
          <Link
            to={Navigate.publishersOnboardingMultistepPage()}
            className="btn btn-light btn-lg px-5"
          >
            Get Started Now
          </Link>
        </div>
      </section>

      <Footer />

      <style jsx>{`
        .pricing-hero-banner {
          background-image: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url("/assets/images/publishers/banner_3.png");
          background-size: cover;
          background-position: center;
          color: white;
          text-align: center;
          padding: 100px 0;
        }
        
        .text-gradient {
          background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .pricing-card {
          border-radius: 12px;
          overflow: hidden;
          border: none;
          box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
          transition: transform 0.3s ease;
          position: relative;
        }
        
        .pricing-card:hover {
          transform: translateY(-10px);
        }
        
        .pricing-card.popular {
          border: 2px solid #0d6efd;
        }
        
        .popular-badge {
          position: absolute;
          top: -10px;
          right: 36.5%;
          background: #0d6efd;
          color: white;
          padding: 5px 15px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: bold;
        }
        
        .price-display {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 5px;
        }
        
        .price-amount {
          font-size: 2.5rem;
          font-weight: bold;
        }
        
        .price-period {
          font-size: 1rem;
          color: rgba(255,255,255,0.8);
        }
        
        .pricing-features {
          margin: 0;
          padding: 0;
        }
        
        .pricing-features li {
          padding: 10px 0;
          border-bottom: 1px solid rgba(0,0,0,0.05);
        }
        
        .pricing-features li:last-child {
          border-bottom: none;
        }
        
        .feature-card {
          background: white;
          border-radius: 10px;
          box-shadow: 0 5px 10px rgba(0,0,0,0.05);
          transition: transform 0.3s ease;
        }
        
        .feature-card:hover {
          transform: translateY(-5px);
        }
        
        .feature-icon {
          font-size: 2rem;
        }
        
        .accordion-button {
          font-weight: 600;
          padding: 1.5rem;
        }
        
        .accordion-button:not(.collapsed) {
          background-color: rgba(13, 110, 253, 0.1);
          color: #0d6efd;
        }
        
        @media (max-width: 768px) {
          .pricing-hero-banner {
            padding: 60px 0;
          }
          
          .price-amount {
            font-size: 2rem;
          }
        }
      `}</style>
    </>
  );
}

const bannerStyle = {
  backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url("/assets/images/publishers/banner_3.png")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
};

export default PublisherPricingPage;